import { Bot, Loader2, Route } from "lucide-react";
import { useState } from "react";
import { AgentPicker } from "@/components/chat/AgentPicker";
import type { RouteState } from "@/hooks/useChatStreaming";
import type { Agent, RoutedAgentCandidate } from "@/types";

export interface RouteDecisionRowProps {
  state: RouteState;
  /** The routed agent can still be switched (only before the user's second message). */
  canSwitch: boolean;
  agents: Agent[];
  nonProjectAgents: Agent[];
  inProject: boolean;
  /** A suggested agent was picked (uuid) or the user continues without one (null). */
  onPickSuggestion: (agentUuid: string | null) => void;
  /** The routed agent is replaced by another agent (uuid) or by plain chat (null). */
  onSwitch: (agentUuid: string | null) => void;
}

/** Inline note under a new chat's first message: routing in progress, routed agent, or suggestions (MHS-627). */
export function RouteDecisionRow({
  state,
  canSwitch,
  agents,
  nonProjectAgents,
  inProject,
  onPickSuggestion,
  onSwitch,
}: Readonly<RouteDecisionRowProps>) {
  const [switching, setSwitching] = useState(false);
  const picker = (onSelect: (uuid: string | null) => void, selectedAgentUuid: string | null) => (
    <AgentPicker
      agents={agents}
      nonProjectAgents={nonProjectAgents}
      inProject={inProject}
      selectedAgentUuid={selectedAgentUuid}
      onSelect={onSelect}
      emptyLabel="Another agent…"
      align="left"
    />
  );

  if (state.kind === "routing") {
    return (
      <RowShell>
        <Loader2 className="w-4 h-4 animate-spin text-content-tertiary" />
        <span className="text-sm text-content-tertiary">Finding the best agent…</span>
      </RowShell>
    );
  }

  if (state.kind === "suggest") {
    return (
      <RowShell>
        <div className="flex flex-col gap-2 min-w-0">
          <span className="text-sm text-content-secondary">Which agent should answer this?</span>
          <div className="flex flex-wrap items-center gap-2">
            {state.decision.candidates.map((candidate) => (
              <CandidateChip
                key={candidate.uuid}
                candidate={candidate}
                onClick={() => onPickSuggestion(candidate.uuid)}
              />
            ))}
            {picker(onPickSuggestion, null)}
            <button
              type="button"
              onClick={() => onPickSuggestion(null)}
              className="text-sm text-content-tertiary hover:text-content-primary underline-offset-2 hover:underline"
            >
              Continue without an agent
            </button>
          </div>
        </div>
      </RowShell>
    );
  }

  const routedAgent = state.agent;
  const alternatives = state.decision.candidates.filter((c) => c.uuid !== routedAgent.uuid).slice(0, 2);
  const switchable = canSwitch && state.sessionUuid !== null; // the switch records against the routed chat
  return (
    <RowShell>
      <div className="flex flex-col gap-2 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-content-secondary">
            Routed to <span className="font-medium text-content-primary">{routedAgent.name}</span>
          </span>
          {state.decision.rationale && <span className="text-content-tertiary">· {state.decision.rationale}</span>}
          {switchable && !switching && (
            <button
              type="button"
              onClick={() => setSwitching(true)}
              className="text-brand-primary underline-offset-2 hover:underline"
            >
              Not right? Switch agent
            </button>
          )}
        </div>
        {switchable && switching && (
          <div className="flex flex-wrap items-center gap-2">
            {alternatives.map((candidate) => (
              <CandidateChip key={candidate.uuid} candidate={candidate} onClick={() => onSwitch(candidate.uuid)} />
            ))}
            {picker((uuid) => {
              if (uuid !== routedAgent.uuid) onSwitch(uuid);
            }, routedAgent.uuid)}
          </div>
        )}
      </div>
    </RowShell>
  );
}

function RowShell({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex gap-4" aria-live="polite">
      <div className="w-8 h-8 rounded-lg bg-brand-primary/10 flex items-center justify-center shrink-0">
        <Route className="w-4 h-4 text-brand-primary" />
      </div>
      <div className="flex-1 min-w-0 flex items-center gap-2 rounded-lg border border-border-secondary bg-surface-secondary/50 px-3 py-2">
        {children}
      </div>
    </div>
  );
}

function CandidateChip({ candidate, onClick }: Readonly<{ candidate: RoutedAgentCandidate; onClick: () => void }>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-full border border-border-secondary bg-surface-primary hover:bg-interactive-hover text-content-primary transition"
    >
      <Bot className="w-3.5 h-3.5 shrink-0" style={{ color: candidate.color }} />
      <span className="truncate max-w-[12rem]">{candidate.name}</span>
    </button>
  );
}
